import md5 from 'crypto-js/md5';
import { enc, AES, PBKDF2 } from 'crypto-js';

const EDITOR_PASSWORD_HASH = '3f55638b4f40f38e89910dbd71874502';
const ADMIN_PASSWORD_HASH = '52826f3a61bb24111a6df3615da9fc35';
const ADMIN_DESIGNER_PASSWORD_HASH = 'f1a86397302e1aa215a62d6738735867';

// Role types: 'admin' | 'admin_designer' | 'editor' | null
export const USER_ROLES = {
	ADMIN: 'admin',
	ADMIN_DESIGNER: 'admin_designer',
	EDITOR: 'editor',
	NONE: null,
};

// Salt for encryption/decryption of credentials
const ENCRYPTION_KEY_SALT = 'asana-time-tracking-credential-salt';
const ENCRYPTION_IV = '1234567890123456';

// LocalStorage keys
const STORAGE_KEYS = {
	USER_ROLE: 'user_role',
	PASSWORD_HASH: 'password_hash',
};

/**
 * Verify password and determine user role
 */
export async function verifyPassword(password) {
	try {
		// Use the md5 function from crypto-js and convert the result to a hex string
		const hash = md5(password).toString(enc.Hex);

		if (hash === ADMIN_PASSWORD_HASH) {
			return { isValid: true, role: USER_ROLES.ADMIN };
		} else if (hash === ADMIN_DESIGNER_PASSWORD_HASH) {
			return { isValid: true, role: USER_ROLES.ADMIN_DESIGNER };
		} else if (hash === EDITOR_PASSWORD_HASH) {
			return { isValid: true, role: USER_ROLES.EDITOR };
		} else {
			return { isValid: false, role: USER_ROLES.NONE };
		}
	} catch (error) {
		console.error('Error verifying password:', error);
		return { isValid: false, role: USER_ROLES.NONE };
	}
}

/**
 * Save user session to localStorage
 */
export function saveUserSession(role, passwordHash) {
	try {
		localStorage.setItem(STORAGE_KEYS.USER_ROLE, role);
		localStorage.setItem(STORAGE_KEYS.PASSWORD_HASH, passwordHash);
	} catch (error) {
		console.error('Error saving user session:', error);
	}
}

/**
 * Get user session from localStorage
 */
export function getUserSession() {
	try {
		const role = localStorage.getItem(STORAGE_KEYS.USER_ROLE);
		const passwordHash = localStorage.getItem(STORAGE_KEYS.PASSWORD_HASH);

		// Verify that the stored hash is still valid
		if (role && passwordHash) {
			if (
				(role === USER_ROLES.ADMIN && passwordHash === ADMIN_PASSWORD_HASH) ||
				(role === USER_ROLES.ADMIN_DESIGNER && passwordHash === ADMIN_DESIGNER_PASSWORD_HASH) ||
				(role === USER_ROLES.EDITOR && passwordHash === EDITOR_PASSWORD_HASH)
			) {
				return { role, isValid: true };
			}
		}

		return { role: USER_ROLES.NONE, isValid: false };
	} catch (error) {
		console.error('Error getting user session:', error);
		return { role: USER_ROLES.NONE, isValid: false };
	}
}

/**
 * Clear user session (logout)
 */
export function clearUserSession() {
	try {
		localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
		localStorage.removeItem(STORAGE_KEYS.PASSWORD_HASH);
	} catch (error) {
		console.error('Error clearing user session:', error);
	}
}

/**
 * Get human-readable role label
 */
export function getRoleLabel(role) {
	const labels = {
		[USER_ROLES.ADMIN]: 'Admin',
		[USER_ROLES.ADMIN_DESIGNER]: 'Admin Designer',
		[USER_ROLES.EDITOR]: 'Editor',
	};
	return labels[role] || role;
}

/**
 * Check if user is admin
 */
export function isAdmin(role) {
	return role === USER_ROLES.ADMIN;
}

/**
 * Check if user is admin designer
 */
export function isAdminDesigner(role) {
	return role === USER_ROLES.ADMIN_DESIGNER;
}

/**
 * Check if user is editor or admin
 */
export function isEditor(role) {
	return role === USER_ROLES.EDITOR || role === USER_ROLES.ADMIN || role === USER_ROLES.ADMIN_DESIGNER;
}

/**
 * Encrypt sensitive data for storage
 */
export function encryptData(data) {
	try {
		// Generate a key from the password
		const key = PBKDF2(ENCRYPTION_KEY_SALT, ENCRYPTION_IV, {
			keySize: 256 / 32,
			iterations: 1000,
		});

		// Encrypt the data
		const encrypted = AES.encrypt(data, key.toString());
		return encrypted.toString();
	} catch (error) {
		console.error('Error encrypting data:', error);
		return '';
	}
}

/**
 * Decrypt sensitive data for display
 */
export function decryptData(encryptedData) {
	try {
		// Generate the same key from the password
		const key = PBKDF2(ENCRYPTION_KEY_SALT, ENCRYPTION_IV, {
			keySize: 256 / 32,
			iterations: 1000,
		});

		// Decrypt the data
		const decrypted = AES.decrypt(encryptedData, key.toString());
		return decrypted.toString(enc.Utf8);
	} catch (error) {
		console.error('Error decrypting data:', error);
		return '[Decryption Failed]';
	}
}
